import React, { useRef } from "react";
import { motion, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";

// Mouse-reactive premium agent network on light theme.
// Blue nodes = AI agents; Gold nodes = human checkpoints.
export const AIAgentNetwork = ({ className = "", reactive = true }) => {
    const reduced = useReducedMotion();
    const hostRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 80, damping: 18, mass: 0.6 });
    const sy = useSpring(my, { stiffness: 80, damping: 18, mass: 0.6 });
    const tx = useTransform(sx, (v) => v * 8);
    const ty = useTransform(sy, (v) => v * 6);
    const tx2 = useTransform(sx, (v) => v * -4);
    const ty2 = useTransform(sy, (v) => v * -3);

    const onMove = (e) => {
        if (!reactive || reduced) return;
        const r = hostRef.current?.getBoundingClientRect();
        if (!r) return;
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        mx.set(nx);
        my.set(ny);
    };

    return (
        <div
            ref={hostRef}
            onMouseMove={onMove}
            className={`relative ${className}`}
            aria-hidden="true"
            data-testid="ai-agent-network-graphic"
        >
            <motion.svg
                viewBox="0 0 600 480"
                className="w-full h-full"
                preserveAspectRatio="xMidYMid meet"
                style={{ x: tx, y: ty }}
            >
                <defs>
                    <radialGradient id="glowBlue" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(37,99,235,0.32)" />
                        <stop offset="60%" stopColor="rgba(37,99,235,0.10)" />
                        <stop offset="100%" stopColor="rgba(37,99,235,0)" />
                    </radialGradient>
                    <radialGradient id="glowGold" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(187,118,33,0.32)" />
                        <stop offset="60%" stopColor="rgba(187,118,33,0.10)" />
                        <stop offset="100%" stopColor="rgba(187,118,33,0)" />
                    </radialGradient>
                    <linearGradient id="line" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="rgba(37,99,235,0.6)" />
                        <stop offset="100%" stopColor="rgba(187,118,33,0.55)" />
                    </linearGradient>
                </defs>

                {/* Glow halos */}
                <circle cx="170" cy="110" r="110" fill="url(#glowBlue)" />
                <circle cx="460" cy="360" r="120" fill="url(#glowGold)" />

                {/* Connections */}
                <g
                    stroke="url(#line)"
                    strokeWidth="1.1"
                    fill="none"
                    className={reduced ? "" : "dash-flow"}
                    opacity="0.85"
                >
                    <path d="M120,140 C200,80 280,200 360,160" />
                    <path d="M120,140 C200,260 320,260 380,330" />
                    <path d="M360,160 C420,200 470,260 460,330" />
                    <path d="M380,330 C420,360 440,330 460,330" />
                    <path d="M120,140 C100,260 220,360 380,330" />
                    <path d="M360,160 C300,210 260,260 240,290" />
                    <path d="M240,290 C280,330 340,330 380,330" />
                </g>

                {/* Blue nodes */}
                {bluePositions.map((p, i) => (
                    <g key={`b-${i}`}>
                        <circle
                            cx={p[0]}
                            cy={p[1]}
                            r="14"
                            fill="rgba(37,99,235,0.18)"
                        />
                        <circle
                            cx={p[0]}
                            cy={p[1]}
                            r="6"
                            fill="#2563EB"
                            stroke="rgba(255,255,255,0.85)"
                            strokeWidth="1"
                        />
                    </g>
                ))}
            </motion.svg>

            {/* Foreground gold checkpoints with parallax */}
            <motion.svg
                viewBox="0 0 600 480"
                className="absolute inset-0 w-full h-full pointer-events-none"
                preserveAspectRatio="xMidYMid meet"
                style={{ x: tx2, y: ty2 }}
            >
                <g>
                    <circle cx="460" cy="330" r="22" fill="rgba(187,118,33,0.18)" />
                    <circle
                        cx="460"
                        cy="330"
                        r="9"
                        fill="#BB7621"
                        stroke="#FFFFFF"
                        strokeWidth="1.2"
                    />
                </g>
                <g>
                    <circle cx="240" cy="290" r="16" fill="rgba(187,118,33,0.16)" />
                    <circle
                        cx="240"
                        cy="290"
                        r="6.5"
                        fill="#BB7621"
                        stroke="#FFFFFF"
                        strokeWidth="1"
                    />
                </g>
            </motion.svg>
        </div>
    );
};

const bluePositions = [
    [120, 140],
    [360, 160],
    [380, 330],
    [180, 220],
    [300, 110],
    [430, 220],
];

export default AIAgentNetwork;
